/**
 * Flow State Rewards — client-side behavior.
 *   - QR code rendering (uses qrcode-generator from CDN)
 *   - Referral link copy + native share
 *
 * Loaded once per page from sections/flow-state-rewards-member.liquid.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // QR code rendering
  // ---------------------------------------------------------------------------
  var QR_CDN = 'https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js';
  var qrLibLoading = null;

  function loadQrLib() {
    if (typeof window.qrcode === 'function') return Promise.resolve();
    if (qrLibLoading) return qrLibLoading;
    qrLibLoading = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = QR_CDN;
      s.async = true;
      s.onload = function () { resolve(); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return qrLibLoading;
  }

  function renderQRCodes(scope) {
    var root = scope || document;
    var nodes = root.querySelectorAll('[data-er-qr]:not([data-er-qr-rendered])');
    if (!nodes.length) return;

    loadQrLib().then(function () {
      nodes.forEach(function (el) {
        var url = el.getAttribute('data-url');
        if (!url) return;
        try {
          var qr = window.qrcode(0, 'M');
          qr.addData(url);
          qr.make();
          el.innerHTML = qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
          var svg = el.querySelector('svg');
          if (svg) {
            svg.setAttribute('width', '100%');
            svg.setAttribute('height', '100%');
            svg.style.display = 'block';
          }
          el.setAttribute('data-er-qr-rendered', '1');
        } catch (err) {
          if (window.console) console.warn('flow-state-qr render failed', err);
        }
      });
    }).catch(function () {});
  }

  // ---------------------------------------------------------------------------
  // Referral copy / share
  // ---------------------------------------------------------------------------
  function flashToast(toast, msg) {
    if (!toast) return;
    toast.textContent = msg;
    toast.classList.add('is-visible');
    clearTimeout(toast.__t);
    toast.__t = setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 2400);
  }

  function fallbackCopy(input, text) {
    if (!input) {
      window.prompt('Copy your referral link:', text);
      return false;
    }
    input.removeAttribute('readonly');
    input.select();
    input.setSelectionRange(0, text.length);
    var ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (_) {}
    input.setAttribute('readonly', 'readonly');
    input.blur();
    return ok;
  }

  function copyToClipboard(text, onSuccess, onFail, input) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(onSuccess).catch(function () {
        if (fallbackCopy(input, text)) onSuccess(); else onFail();
      });
    } else {
      if (fallbackCopy(input, text)) onSuccess(); else onFail();
    }
  }

  function bindReferral(root) {
    if (root.hasAttribute('data-er-referral-bound')) return;
    root.setAttribute('data-er-referral-bound', '1');

    var url = root.getAttribute('data-url') || '';
    if (!url) return;

    var shareTitle = root.getAttribute('data-share-title') || '';
    var shareText = root.getAttribute('data-share-text') || '';
    var copySuccess = root.getAttribute('data-copy-success') || 'Link copied';

    var input = root.querySelector('[data-er-referral-input]');
    var copyBtn = root.querySelector('[data-er-referral-copy]');
    var copyLabel = root.querySelector('[data-er-referral-copy-label]');
    var shareBtn = root.querySelector('[data-er-referral-share]');
    var toast = root.querySelector('[data-er-referral-toast]');

    function onCopied() {
      flashToast(toast, copySuccess);
      if (copyLabel) {
        var original = copyLabel.textContent;
        copyLabel.textContent = copySuccess;
        setTimeout(function () { copyLabel.textContent = original; }, 1800);
      }
    }
    function onCopyFail() {
      window.prompt('Copy your referral link:', url);
    }

    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        copyToClipboard(url, onCopied, onCopyFail, input);
      });
    }
    if (input) {
      input.addEventListener('focus', function () { input.select(); });
    }
    if (shareBtn) {
      shareBtn.addEventListener('click', function () {
        if (navigator.share) {
          navigator.share({ title: shareTitle, text: shareText, url: url }).catch(function () {});
        } else {
          copyToClipboard(url, onCopied, onCopyFail, input);
        }
      });
    }
  }

  function bindAllReferrals(scope) {
    var root = scope || document;
    var nodes = root.querySelectorAll('[data-er-referral]');
    for (var i = 0; i < nodes.length; i++) bindReferral(nodes[i]);
  }

  // ---------------------------------------------------------------------------
  // Featured products carousel — arrow controls + state sync
  // ---------------------------------------------------------------------------
  function bindCarousel(root) {
    if (root.hasAttribute('data-er-carousel-bound')) return;
    root.setAttribute('data-er-carousel-bound', '1');

    var track = root.querySelector('[data-er-carousel-track]');
    var prev = root.querySelector('[data-er-carousel-prev]');
    var next = root.querySelector('[data-er-carousel-next]');
    if (!track) return;

    function stepSize() {
      var first = track.querySelector('.er-featured__item');
      if (!first) return track.clientWidth;
      var rect = first.getBoundingClientRect();
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return rect.width + gap;
    }

    function updateArrows() {
      if (!prev || !next) return;
      var maxScroll = track.scrollWidth - track.clientWidth - 1;
      var atStart = track.scrollLeft <= 1;
      var atEnd = track.scrollLeft >= maxScroll;
      prev.toggleAttribute('disabled', atStart);
      next.toggleAttribute('disabled', atEnd);
    }

    if (prev) {
      prev.addEventListener('click', function () {
        track.scrollBy({ left: -stepSize(), behavior: 'smooth' });
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        track.scrollBy({ left: stepSize(), behavior: 'smooth' });
      });
    }

    track.addEventListener('scroll', function () {
      clearTimeout(track.__arrT);
      track.__arrT = setTimeout(updateArrows, 80);
    }, { passive: true });

    window.addEventListener('resize', function () {
      clearTimeout(track.__arrT);
      track.__arrT = setTimeout(updateArrows, 120);
    });

    updateArrows();
  }

  function bindAllCarousels(scope) {
    var root = scope || document;
    var nodes = root.querySelectorAll('[data-er-carousel]');
    for (var i = 0; i < nodes.length; i++) bindCarousel(nodes[i]);
  }

  // ---------------------------------------------------------------------------
  // Init + re-init on Shopify section events
  // ---------------------------------------------------------------------------
  function init(scope) {
    renderQRCodes(scope);
    bindAllReferrals(scope);
    bindAllCarousels(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); }, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });
  document.addEventListener('shopify:block:select', function (e) {
    init(e.target || document);
  });
})();
